<template>
    <div>
        <ConfirmDialog
            :is-open="deletePlateOrderItemDialog"
            :data="plateOrderItem"
            @on-confirm="removePlateOrderItem(plateOrderItem)"
            @on-abort="closeDeletePlateOrderItemDialog"
        >
            <template #title>
                Usuwanie płyty
            </template>
            <template #content>
                Czy na pewno chcesz usunąć płytę?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="1100px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onAbort"
            @click:outside="onAbort"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <strong> Zlecenie: {{ plateOrder.jobNumber }} Montaż: {{ plateOrder.plateNumber }} Maszyna: {{ plateOrder.machineCode }} </strong>
                    </h1>
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col>
                            <v-card>
                                <v-card-text>
                                    <v-data-table
                                        v-model="plateOrderItemsSelectedForChange"
                                        :headers="headers"
                                        :items="plateOrderItems"
                                        :items-per-page="5"
                                        :page.sync="currentPlateOrdersPaginationPage"
                                        :footer-props="{
                                            itemsPerPageText: 'Płyt na stronie',
                                            showFirstLastPage: false
                                        }"
                                        dense
                                        fixed-header
                                        :show-select="canUpdatePlateOrderItem"
                                        :mobile-breakpoint="0"
                                        @toggle-select-all="toggleSelectAll($event)"
                                    >
                                        <template #item.data-table-select="{item}">
                                            <v-simple-checkbox v-model="item.checked" :disabled="!!item.checkedById" :ripple="false" @input="updateSelectedItems(item, $event)" />
                                        </template>
                                        <template #item.colour="{item}">
                                            {{ item.colour }}
                                        </template>
                                        <template #item.side="{item}">
                                            {{ item.side }}
                                        </template>
                                        <template #item.checked="{item}">
                                            <v-icon
                                                v-if="item.checked"
                                                small
                                                color="success"
                                            >
                                                mdi-check
                                            </v-icon>
                                            <v-icon v-else small color="error">
                                                mdi-close
                                            </v-icon>
                                        </template>
                                        <template #item.checkedBy="{item}">
                                            <template v-if="item.checkedBy">
                                                <template v-if="item.checkedBy.firstName && item.checkedBy.lastName">
                                                    {{ item.checkedBy.firstName }} {{ item.checkedBy.lastName }}
                                                </template>
                                                <template v-else>
                                                    {{ item.checkedBy.username }}
                                                </template>
                                            </template>
                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                        <template #item.checkedDate="{item}">
                                            {{ formatDate(item.checkedDate) }}
                                        </template>
                                        <template v-if="canDeletePlateOrderItem" #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            v-on="on"
                                                            @click="showDeletePlateOrderItemDialog(item.id)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                        <template slot="no-results" :value="true">
                                            <p class="text-center">
                                                Brak danych
                                            </p>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12" lg="8">
                                        <v-card>
                                            <v-card-text>
                                                <v-row>
                                                    <v-col>
                                                        <v-data-table
                                                            :headers="plateOrderCauses"
                                                            :items="plateOrder.plateOrderReasons"
                                                            :mobile-breakpoint="0"
                                                            :items-per-page="5"
                                                            dense
                                                            :page.sync="currentCausesPaginationPage"
                                                            :footer-props="{
                                                                itemsPerPageText: 'Przyczyn na stronie',
                                                                showFirstLastPage: false
                                                            }"
                                                            name="causesData"
                                                        >
                                                            <template #item.reason="{item}">
                                                                {{ item.reason }}
                                                            </template>
                                                        </v-data-table>
                                                    </v-col>
                                                </v-row>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" lg="4">
                                        <v-card>
                                            <v-card-text>
                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr>
                                                            <th>Data zamówienia:</th>
                                                            <td>{{ formatDate(plateOrder.createdAt) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Zamawiający:</th>
                                                            <td>{{ plateOrder.createdByFirstName }} {{ plateOrder.createdByLastName }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onAbort"
                    >
                        Zamknij
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        v-if="canUpdatePlateOrderItem"
                        :loading="actionLoading"
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        @click="savePlateOrder()"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
    name: 'PlateOrderItemsDialog',
    components: {
        ConfirmDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        plateOrderData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            plateOrderItem: {},
            deletePlateOrderItemLoading: false,
            deletePlateOrderItemDialog: false,
            plateOrder: {},
            plateOrderItemsSelectedForChange: [],
            actionLoading: false,
            currentPlateOrdersPaginationPage: null,
            currentCausesPaginationPage: null,
            plateOrderItems: [],
            headers: [
                {
                    text: 'Kod płyty',
                    value: 'plateCode',
                    sortable: false
                },
                {
                    text: 'Kolor',
                    value: 'colour',
                    sortable: false,
                    width: 100
                },
                {
                    text: 'Strona',
                    align: 'center',
                    sortable: false,
                    value: 'side'
                },
                {
                    text: 'Wykonana',
                    align: 'center',
                    sortable: false,
                    value: 'checked'
                },
                {
                    text: 'Wykonana przez',
                    align: 'center',
                    sortable: false,
                    value: 'checkedBy'
                },
                {
                    text: 'Data wykonania',
                    align: 'center',
                    sortable: false,
                    value: 'checkedDate'
                },
            ],
            plateOrderCauses: [
                {
                    text: 'Przyczyna zamówienia',
                    value: 'reason',
                    sortable: false,
                }
            ]
        }
    },
    computed: {
        canDeletePlateOrderItem() {
            return hasUserPermission('plate_orders_delete')
        },
        canUpdatePlateOrderItem() {
            return hasUserPermission('plate_orders_update')
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (this.plateOrderData === null) return
                this.plateOrder = JSON.parse(JSON.stringify(this.plateOrderData))
                this.plateOrderItems = JSON.parse(JSON.stringify(this.plateOrderData.plateOrderItems))
                this.plateOrderItems.forEach(item => {
                    if (item.checked) {
                        this.plateOrderItemsSelectedForChange.push(item)
                    }
                })
            } else {
                setTimeout(() => {
                    this.plateOrderItem = {}
                    this.plateOrder = {}
                    this.plateOrderItems = []
                    this.plateOrderItemsSelectedForChange = []
                }, 500)
            }
        }
    },
    created() {
        if (hasUserPermission('plate_orders_delete')) {
            this.headers.push(
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            )
        }
    },
    methods: {
        updateSelectedItems(item, value) {
            if (value) {
                this.plateOrderItemsSelectedForChange.push(item)
            } else {
                this.plateOrderItemsSelectedForChange.splice(this.plateOrderItemsSelectedForChange.indexOf(item.id), 1)
            }
        },
        toggleSelectAll(selectAllCheckbox) {
            selectAllCheckbox.items.forEach(item => {
                if (!item.checkedBy) item.checked = !item.checked
            })
        },
        async getPlateOrder(plateOrderId) {
            return await this.$store.dispatch('prepress/getPlateOrder', plateOrderId)
                .then(response => {
                    this.plateOrder = JSON.parse(JSON.stringify(response.data))
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getPlateOrderItem(plateOrderItemId) {
            return await this.$store.dispatch('prepress/getPlateOrderItem', plateOrderItemId)
                .then(response => {
                    this.plateOrderItem = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDeletePlateOrderItemDialog(plateOrderItemId) {
            this.getPlateOrderItem(plateOrderItemId)
                .then(() => {
                    this.deletePlateOrderItemDialog = true
                })
        },
        removePlateOrderItem() {
            this.deletePlateOrderItemLoading = true
            this.$store.dispatch('prepress/deletePlateOrderItem', this.plateOrderItem.id )
                .then(() => {
                    this.getPlateOrder(this.plateOrder.id)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeletePlateOrderItemDialog()
                })
        },
        closeDeletePlateOrderItemDialog() {
            this.deletePlateOrderItemLoading = false
            this.deletePlateOrderItemDialog = false
        },
        savePlateOrder() {
            this.actionLoading = true
            this.plateOrder.plateOrderItems = this.plateOrderItemsSelectedForChange
            this.$store.dispatch('prepress/updatePlateOrder', this.plateOrder)
                .then(() => {
                    this.$emit('get-plate-orders', this.plateOrder.id)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onAbort()
                })
        },
        onAbort() {
            this.$emit('on-close')
        },
    }
}
</script>